import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type {
  Stripe,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
} from '@stripe/stripe-js';
import { GOOGLE_PAY_PAYMENT_CONFIG } from '@/constants/stripe';
import {
  failExpressCheckoutSheet,
  getStripeExpressCheckoutShippingConfig,
  isExpressPaymentFailure,
} from '@/utils/stripe';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import {
  EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS,
  EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON,
  useExpressReadyTimeout,
} from '@/hooks/useExpressReadyTimeout';
import { useStripeWalletShippingZipTax } from '@/hooks/useStripeWalletShippingZipTax';
import {
  useStableStripeExpressElementsOptions,
  useSyncStripeElementsAmount,
} from '@/hooks/useStableStripeExpressElements';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { logError } from '@/utils/logger';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

interface GooglePayStripeFormProps {
  onInvalidTaxClick?: () => void;
  isExpress?: boolean;
}

interface GooglePayButtonUIProps {
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onShippingAddressChange?: (event: StripeExpressCheckoutElementShippingAddressChangeEvent) => void;
  onCancel?: () => void;
  onLoadError?: (error?: unknown) => void;
  onInvalidTaxClick?: () => void;
}

const GooglePayButtonUI = ({
  onReady,
  onConfirm,
  onShippingAddressChange,
  onCancel,
  onLoadError,
  onInvalidTaxClick,
}: GooglePayButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
    featureFlags,
    sessionStartedWithoutValidTax,
  } = context;
  // Options carry `emailRequired` + `shippingAddressRequired` for US/CA
  // sessions where the wallet-tax pipeline is on, so we can recompute tax
  // in-sheet.
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const { t } = useTranslate();
  // Country only — email keystrokes must not remount ExpressCheckoutElement.
  const taxCountry = customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '';

  const expressCheckoutOptions = useMemo(
    () =>
      isWalletZipTaxActive
        ? {
            ...GOOGLE_PAY_PAYMENT_CONFIG,
            // Stripe rejects `emailRequired` on `click.resolve()` once any
            // other collection param is on options — so when we layer
            // `shippingAddressRequired` in via
            // `getStripeExpressCheckoutShippingConfig` we MUST move
            // `emailRequired` to options too.
            emailRequired: true,
            ...getStripeExpressCheckoutShippingConfig(customer, featureFlags, sessionStartedWithoutValidTax),
          }
        : GOOGLE_PAY_PAYMENT_CONFIG,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- customer email must not remount ECE
    [isWalletZipTaxActive, taxCountry, featureFlags, sessionStartedWithoutValidTax]
  );

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (isWalletZipTaxActive) {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
      // `emailRequired` is on `expressCheckoutOptions` in this path (see above).
      event.resolve();
      return;
    }
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
      event.resolve({ emailRequired: true });
    } else {
      if (onInvalidTaxClick) {
        onInvalidTaxClick();
      } else {
        const errorMessageKey = getZipCodeErrorMessageKey(customer);
        setError(t(errorMessageKey));
      }
      event.reject();
    }
  };

  return (
    <ExpressCheckoutElement
      onClick={handleClick}
      onShippingAddressChange={onShippingAddressChange}
      onConfirm={onConfirm}
      onCancel={onCancel}
      onReady={onReady}
      onLoadError={onLoadError}
      options={expressCheckoutOptions}
    />
  );
};

interface GooglePayButtonProps {
  /** The instance behind the enclosing `<Elements>` — the payment must run on it. */
  stripe: Stripe;
  onReady: () => void;
  onUnavailable: (reason: string) => void;
  onInvalidTaxClick?: () => void;
}

const GooglePayButton = ({ stripe, onReady, onUnavailable, onInvalidTaxClick }: GooglePayButtonProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { runPayment, setError },
    template,
    pricing,
  } = context;
  const elements = useElements();
  useSyncStripeElementsAmount(elements, pricing.priceBreakdown.priceTotal);
  // ACDEV-25067: gated internally on `WALLET_ZIP_TAX` — when FF-OFF, the hook
  // returns `onShippingAddressChange: undefined` and `capturedWalletTaxRef.current`
  // stays null, so wiring these props unconditionally is a functional no-op
  // for the pre-branch path.
  const {
    onShippingAddressChange: handleShippingAddressChange,
    capturedWalletTaxRef,
    applyWalletTaxToCheckout,
  } = useStripeWalletShippingZipTax(elements);
  const compactExpressForms = template.features.compactExpressForms;
  const { t } = useTranslate();

  const handleLoadError = (error?: unknown) => {
    logError('Google Pay load error', error);
    if (error && compactExpressForms) {
      mixpanelService.track(
        MixpanelEvents.PAYMENT_NOT_SUPPORTED,
        getMixpanelProperties(context) as Record<string, unknown>
      );
    }
    onUnavailable('load_error');
  };

  /**
   * Stripe ECE fires `onReady` even when the configured wallet is
   * unavailable on this device — it just leaves the button unpainted.
   * Per Stripe's type:
   *   availablePaymentMethods: undefined | AvailablePaymentMethods
   *   "...undefined if no payment methods can show."
   *
   * So unavailable manifests as EITHER:
   *   - `availablePaymentMethods === undefined`, OR
   *   - `availablePaymentMethods.googlePay === false`
   *
   * Without this check we'd mark the form as ready, never call
   * `useExpressWalletFallback`, and strand the FTD / last-method
   * PREFERRED_PAYMENT_METHOD user on an invisible primary CTA.
   *
   * (`onLoadError` only fires for SDK-load failures, not for the
   * "device doesn't support Google Pay" case.)
   */
  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const googlePayAvailable = event.availablePaymentMethods?.googlePay === true;
    if (!googlePayAvailable) {
      onUnavailable('device_unsupported');
      return;
    }
    onReady();
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      notifyPaymentIntentClick(context, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
      // `capturedWalletTaxRef.current?.zipCode` is undefined on the pre-branch path
      // (FF-OFF); the payment handler drops undefined zipCode, so the wire
      // format matches pre-branch when the FF is OFF.
      const result = await runPayment(
        { event, elements: elements!, stripe, zipCode: capturedWalletTaxRef.current?.zipCode },
        PaymentMethodEnum.GOOGLE_PAY
      );
      // `runPayment` surfaces its own errors and resolves `undefined`; the
      // Google Pay sheet still needs to hear about the failure or it spins
      // until Google's timeout.
      if (isExpressPaymentFailure(result)) {
        failExpressCheckoutSheet(event);
      }
    } catch {
      failExpressCheckoutSheet(event);
      applyWalletTaxToCheckout();
      setError(t('somethingWentWrong'));
    }
  };

  return (
    <GooglePayButtonUI
      onReady={handleReady}
      onConfirm={handleConfirm}
      onShippingAddressChange={handleShippingAddressChange}
      onCancel={applyWalletTaxToCheckout}
      onLoadError={handleLoadError}
      onInvalidTaxClick={onInvalidTaxClick}
    />
  );
};

export const GooglePayStripeForm = ({ onInvalidTaxClick, isExpress }: GooglePayStripeFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    pricing,
    actions: { setError },
    template,
  } = context;
  const compactExpressForms = template.features.compactExpressForms;
  const { t } = useTranslate();
  const [state, setState] = useState<WalletReadyState>('loading');
  // Bridges Stripe ECE's `onLoadError` to the active template so the
  // FTD / last-method PREFERRED_PAYMENT_METHOD branches can swap to
  // DEFAULT when Google Pay was advertised by the BE but Stripe
  // rejects it at runtime (Pay.js init failure / device probe false).
  const reportWalletAvailability = useExpressWalletFallback();
  const mountTimeRef = useRef(Date.now());
  const elementsOptions = useStableStripeExpressElementsOptions(
    pricing.priceBreakdown.priceTotal,
    pricing.currencyCode
  );

  const handleReady = () => {
    reportWalletAvailability(true, {
      wallet: 'google_pay',
      provider: ProviderEnum.STRIPE,
      loadTimeMs: Date.now() - mountTimeRef.current,
    });
    setState('ready');
  };
  const handleUnavailable = (reason: string, timeoutMs?: number) => {
    reportWalletAvailability(false, {
      wallet: 'google_pay',
      provider: ProviderEnum.STRIPE,
      reason,
      loadTimeMs: Date.now() - mountTimeRef.current,
      timeoutMs,
    });
    setState('unavailable');
  };

  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: PaymentMethodEnum.GOOGLE_PAY,
    isExpress: true,
  });

  // Stripe.js terminal failure (null / missing key): ECE never mounts, so
  // onReady/onLoadError cannot fire — collapse the shell instead of spinning.
  useEffect(() => {
    if (isStripeLoading || stripe) return;
    handleUnavailable('init_error');
  }, [isStripeLoading, stripe]);

  // ECE mounted but silent (hung Google Pay probe in odd Chromium forks).
  // Armed only after Stripe.js is ready so cold SDK load isn't double-counted
  // against this looser hang budget. Reason is OUR client hang-breaker — not
  // a Stripe-emitted timeout (Stripe never fires one for silence).
  useExpressReadyTimeout(
    state === 'loading' && Boolean(stripe) && !isStripeLoading,
    () => handleUnavailable(EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON, EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS),
    EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS
  );

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress={isExpress} />;
  }

  try {
    return (
      <WalletButtonShell
        state={state}
        placeholder={<GooglePayButtonPlaceholder />}
        testId="wallet-btn-stripe-google-tile"
        usePlaceholderAsVisual
      >
        {stripe && !isStripeLoading && (
          <Elements stripe={stripe} options={elementsOptions}>
            <GooglePayButton
              stripe={stripe}
              onReady={handleReady}
              onUnavailable={handleUnavailable}
              onInvalidTaxClick={onInvalidTaxClick}
            />
          </Elements>
        )}
      </WalletButtonShell>
    );
  } catch (error) {
    logError('Google Pay Stripe Elements initialization error', error);
    if (compactExpressForms) {
      mixpanelService.track(
        MixpanelEvents.PAYMENT_NOT_SUPPORTED,
        getMixpanelProperties(context) as Record<string, unknown>
      );
      setError(t('paymentMethodNotAvailable'));
    }
    return null;
  }
};
